import { Calendar, Grid3x3, Inbox, ListTodo, Search, Settings, Sparkles, Sun } from "@app/ui";
import { NavLink, useLocation } from "react-router-dom";
import type { I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { sidebarPartVisible } from "../../lib/sidebar-visibility.js";
import { getSettingsTarget } from "../../routes/settings/last-settings-page.js";
import { visibleCategories } from "../../routes/settings/nav.js";
import { useSettingsStore } from "../../stores/index.js";
import type { SidebarPart } from "../../stores/settings.store.js";

type NavPart = Exclude<SidebarPart, "filters">;

const LABEL_KEY: Record<NavPart, I18nKey> = {
  today: "navigation.part.today",
  calendar: "navigation.part.calendar",
  tasks: "navigation.part.tasks",
  priorities: "navigation.part.priorities",
  inbox: "navigation.part.inbox",
  search: "navigation.part.search",
  claude: "navigation.part.claude",
};

function icon(part: NavPart) {
  if (part === "today") return Sun;
  if (part === "tasks") return ListTodo;
  if (part === "calendar") return Calendar;
  if (part === "priorities") return Grid3x3;
  if (part === "inbox") return Inbox;
  if (part === "search") return Search;
  return Sparkles;
}

/**
 * Mobile bottom navigation row — the sidebar's nav icons relocated to a fixed
 * bar (the sidebar drawer keeps only the filters). The inbox icon is dropped:
 * on the calendar a right-edge swipe opens the inbox instead. Settings rides
 * at the end.
 */
export function BottomNav() {
  const { t } = useI18n();
  const lastCalendarView = useSettingsStore((s) => s.lastCalendarView);
  const developerMode = useSettingsStore((s) => s.developerMode);
  const sidebarVisible = useSettingsStore((s) => s.sidebarVisible);
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const prioritiesOverride = useSettingsStore((s) => s.prioritiesNavVisibleOverride);
  const sidebarNavOrder = useSettingsStore((s) => s.sidebarNavOrder);
  const location = useLocation();
  const onCalendar = location.pathname.startsWith("/calendar");

  const parts = sidebarNavOrder.filter(
    (p): p is NavPart => p !== "filters" && p !== "inbox" && sidebarPartVisible(p, {
      visible: sidebarVisible,
      matrixScope,
      prioritiesOverride,
    }),
  );

  function target(part: NavPart): string {
    if (part === "calendar") return `/calendar/${lastCalendarView}`;
    if (part === "claude") return "/ai";
    return `/${part}`;
  }

  function active(part: NavPart): boolean {
    if (part === "calendar") return onCalendar;
    if (part === "claude") return location.pathname.startsWith("/ai");
    return location.pathname.startsWith(`/${part}`);
  }

  const item = (isActive: boolean) =>
    `flex h-12 flex-1 items-center justify-center rounded-md ${isActive ? "bg-accent text-foreground" : "text-muted-foreground"}`;
  const settingsTarget = getSettingsTarget(visibleCategories(developerMode).map((c) => c.route));

  return (
    <nav
      aria-label={t("nav.main")}
      className="flex shrink-0 items-center gap-1 border-t bg-card px-2 py-1"
      style={{ paddingBottom: "max(4px, env(safe-area-inset-bottom))" }}
    >
      {parts.map((part) => {
        const Icon = icon(part);
        const label = t(LABEL_KEY[part]);
        return (
          <NavLink key={part} to={target(part)} title={label} aria-label={label} className={item(active(part))}>
            <Icon size={22} />
          </NavLink>
        );
      })}
      <NavLink to={settingsTarget} title={t("common.settings")} aria-label={t("common.settings")} className={({ isActive }) => item(isActive)}>
        <Settings size={22} />
      </NavLink>
    </nav>
  );
}
